"use client";

import { HomePageQueryResult } from "@/sanity.types";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

type ValuesProps = {
	values: NonNullable<HomePageQueryResult>["connectingQualitySection"]["values"];
};

export default function Values({ values }: ValuesProps) {
	const [visibleCount, setVisibleCount] = useState(0);
	const sectionRef = useRef<HTMLDivElement | null>(null);
	const visibleCountRef = useRef(0);

	useEffect(() => {
		const handleScroll = () => {
			if (!sectionRef.current) return;

			const section = sectionRef.current.closest("section[id='connectingQuality']") as HTMLElement | null;
			if (!section) return;

			const sectionRect = section.getBoundingClientRect();
			const sectionTop = sectionRect.top;
			const sectionHeight = sectionRect.height;
			const viewportHeight = window.innerHeight;

			const maxVisibleCount = values.length;

			if (sectionTop >= 0) {
				setVisibleCount(0);
				visibleCountRef.current = 0;
				section.style.height = `${maxVisibleCount * 100}vh`;
				return;
			}

			const scrollableDistance = sectionHeight - viewportHeight;
			if (scrollableDistance <= 0) return;

			const scrollProgress = Math.min(1, -sectionTop / scrollableDistance);
			const newVisibleCount = Math.min(maxVisibleCount, Math.floor(scrollProgress * maxVisibleCount) + 1);

			if (newVisibleCount > visibleCountRef.current) {
				setVisibleCount(newVisibleCount);
				visibleCountRef.current = newVisibleCount;
				section.style.height = `${maxVisibleCount * 100}vh`;
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, [values.length]);

	if (!values?.length) return null;

	return (
		<div ref={sectionRef} className="space-y-14">
			{values.map((value, index) => (
				<motion.div
					key={index}
					initial={{ y: 30, opacity: 0 }}
					animate={{
						y: index < visibleCount ? 0 : 30,
						opacity: index < visibleCount ? 1 : 0,
					}}
					transition={{
						duration: 0.6,
						ease: "easeOut",
						delay: index * 0.1,
					}}
				>
					<div className="flex items-start gap-x-5 sm:gap-x-7">
						<motion.div
							className="bg-olive-green-light flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-full sm:h-11.5 sm:w-11.5"
							initial={{ rotate: 0, scale: 0.8 }}
							animate={{
								rotate: index < visibleCount ? 180 : 0,
								scale: index < visibleCount ? 1 : 0.8,
							}}
							transition={{
								duration: 0.6,
								ease: "easeOut",
								delay: index * 0.1,
							}}
						>
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none" className="h-4 w-4 sm:h-6 sm:w-6">
								<path
									d="M8.29443 12.5593C8.29443 11.1236 7.13055 9.95972 5.69482 9.95972L3.89502 9.95972C3.4533 9.95972 3.09521 9.60163 3.09521 9.15991C3.09521 8.71819 3.4533 8.36011 3.89502 8.36011L5.69482 8.36011C7.13055 8.36011 8.29443 7.19622 8.29443 5.7605L8.29443 3.96069C8.29443 3.51897 8.65252 3.16089 9.09424 3.16089C9.53596 3.16089 9.89404 3.51897 9.89404 3.96069L9.89404 5.7605C9.89404 7.19622 11.0579 8.36011 12.4937 8.36011L14.2954 8.36011C14.7371 8.36011 15.0952 8.71819 15.0952 9.15991C15.0952 9.60163 14.7371 9.95972 14.2954 9.95972L12.4946 9.95972C11.0584 9.95972 9.89404 11.124 9.89404 12.5603L9.89404 14.3611C9.89404 14.8028 9.53596 15.1609 9.09424 15.1609C8.65252 15.1609 8.29443 14.8028 8.29443 14.3611L8.29443 12.5593Z"
									fill="#504120"
								/>
								<rect
									x="0.694336"
									y="9.96436"
									width="1.6"
									height="16.6114"
									transform="rotate(-90 0.694336 9.96436)"
									fill="#504120"
								/>
								<rect
									x="9.89062"
									y="17.5627"
									width="1.6"
									height="16.6114"
									transform="rotate(180 9.89062 17.5627)"
									fill="#504120"
								/>
							</svg>
						</motion.div>
						<p className="text-body-m text-white sm:text-[1.125rem]">{value.text}</p>
					</div>
				</motion.div>
			))}
		</div>
	);
}
